import { IconButton, IconButtonSize } from "shared/components";
import { Card } from "shared/components/Card";
import { Delete01Icon, Edit02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import {
  hasValidationError,
  selectSortedQuestions,
  useSurveyStore
} from "../../../../store";
import { toLabel } from "../../constants";
import { Show } from "shared/components/helpers/Show";
import { DraftSurveyTemplateCondition } from "../../../../types";

const PreviewBox = ({
  children,
  className
}: {
  children: string;
  className?: string;
}) => {
  return (
    <Card
      as="div"
      className={cx(
        "block px-3.5 py-2.5 min-h-[40px] text-sm font-medium text-content-1000 h-fit",
        className
      )}
    >
      {children}
    </Card>
  );
};

export const ConditionCard = ({
  condition,
  index,
  onEdit,
  onDelete
}: {
  condition: DraftSurveyTemplateCondition;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const questions = useSurveyStore(selectSortedQuestions);
  const validationErrors = useSurveyStore((state) => state.validationErrors);

  const parentQuestion = questions.find(
    (q) => q.internal_id === condition.parent_question_internal_id
  );

  const childQuestions = condition.child_question_internal_ids
    .map((id) => questions.find((q) => q.internal_id === id))
    .filter((q): q is (typeof questions)[number] => Boolean(q));

  const hasError = hasValidationError(validationErrors, "condition", index);

  return (
    <Card
      as="article"
      className={cx(
        "p-4 !bg-background-900 space-y-4",
        hasError && "!border-danger-500"
      )}
    >
      <Card as="section" className="p-5">
        <h3 className="text-base font-bold text-content-1000 mb-5 border-b border-alpha-40 pb-5">
          Condition {index + 1}
        </h3>

        {/* IF row */}
        <div className="flex items-start gap-3 w-full">
          <span className="text-primary-500 font-bold text-sm w-8 shrink-0">
            if
          </span>
          <div className="flex-grow">
            <PreviewBox className="mb-2">
              {parentQuestion
                ? `${parentQuestion.index + 1}. ${parentQuestion.question}`
                : "No question selected"}
            </PreviewBox>
            <Show if={Object.keys(condition.condition).length > 0}>
              <div className="grid gap-2 grid-cols-[140px_1fr]">
                {Object.entries(condition.condition).map(([key, value]) => (
                  <PreviewBox key={key}>{toLabel(String(value))}</PreviewBox>
                ))}
              </div>
            </Show>
          </div>
        </div>
      </Card>
      {/* THEN row */}
      <Card as="section" className="p-5">
        <div className="flex items-start gap-3 w-full">
          <span className="text-primary-500 font-bold text-sm w-8 shrink-0">
            then
          </span>
          <div className="grid gap-2 grid-cols-[140px_1fr] flex-1">
            <Show if={Boolean(condition.action)}>
              <PreviewBox>{toLabel(condition.action)}</PreviewBox>
            </Show>
            {childQuestions.map((q) => (
              <PreviewBox key={q.internal_id}>
                {`${q.index + 1}. ${q.question}`}
              </PreviewBox>
            ))}
          </div>
        </div>
      </Card>
      <footer className="flex gap-2 justify-end">
        <IconButton
          size={IconButtonSize.SM}
          HugeIcon={Delete01Icon}
          onClick={onDelete}
          aria-label={`Delete condition ${index + 1}`}
        />
        <IconButton
          size={IconButtonSize.SM}
          HugeIcon={Edit02Icon}
          onClick={onEdit}
          aria-label={`Edit condition ${index + 1}`}
        />
      </footer>
    </Card>
  );
};
